import React, { lazy, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Layout from "./components/Layout";
import NotFound from "./pages/404";
import HomePage from "./pages/HomePage";
import ScrollToTop from "./components/ScrollToTop";
import './App.css';

const Games = lazy(() => import("./pages/Games"));
const Minecraft = lazy(() => import("./pages/Minecraft"));
const FiveM = lazy(() => import("./pages/FiveM"));
const Palworld = lazy(() => import("./pages/palworld"));
const VPSHostingShowcase = lazy(() => import("./pages/ryzen-vps"));
const IntelXeon = lazy(() => import("./pages/intel-xeon"));
const Website = lazy(() => import("./pages/website"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const GDPR = lazy(() => import("./pages/GDPR"));
const AUP = lazy(() => import("./pages/Aup"));
const AboutPage = lazy(() => import("./pages/About"));
const PartnersPage = lazy(() => import("./pages/Partners"));
const Discord = lazy(() => import("./pages/Discord"));
const Storage = lazy(() => import("./pages/Storage"));
const Dedicated = lazy(() => import("./pages/dedicated"));

function LoadingSpinner() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
      <div className="relative w-24 h-24">
        <div className="absolute inset-0 animate-spin">
          <img
            src="/images/rave.png"
            alt="Loading..."
            className="object-contain w-24 h-24"
          />
        </div>
        <p className="absolute text-sm font-medium text-gray-600 -translate-x-1/2 -bottom-8 left-1/2 dark:text-gray-300">
          Loading...
        </p>
      </div>
    </div>
  );
}

// Suspense fallback
function LoadingSkeleton() {
  return (
    <div className="flex items-center justify-center flex-1 p-4">
      <div className="flex space-x-4 animate-pulse">
        <div className="flex-1 py-1 space-y-4">
          <div className="w-3/4 h-4 bg-gray-200 rounded dark:bg-gray-700"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded dark:bg-gray-700"></div>
            <div className="w-5/6 h-4 bg-gray-200 rounded dark:bg-gray-700"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageTransitionLoader() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500); // loading time

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return isLoading ? <LoadingSpinner /> : null;
}

function AppContent() {
  return (
    <>
      <PageTransitionLoader />
      <ScrollToTop />
      <React.Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/games" element={<Games />} />
            <Route path="/minecraft" element={<Minecraft />} />
            <Route path="/fivem" element={<FiveM />} />
            <Route path="/palworld" element={<Palworld />} />
            <Route path="/ryzen-vps" element={<VPSHostingShowcase />} />
            <Route path="/intel-xeon" element={<IntelXeon />} />
            <Route path="/discord" element={<Discord />} />
            <Route path="/website" element={<Website />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/Storage" element={<Storage />} />
            <Route path="/Dedicated" element={<Dedicated />} />
            <Route path="/gdpr" element={<GDPR />} />
            <Route path="/aup" element={<AUP />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </React.Suspense>
    </>
  );
}
function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
